import { useEffect, useRef, useState } from 'react';
import type { WorkTypeOption } from './WorkTypeField';

type WorkTypeRow = { id?: number; name: string };

export const useTenantWorkTypes = (
  tenantId: number | undefined,
  enabled: boolean,
  load?: (
    abortController: AbortController,
    tenantId?: number
  ) => Promise<WorkTypeRow[]>
) => {
  const loadRef = useRef(load);
  loadRef.current = load;
  const [options, setOptions] = useState<WorkTypeOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const fetchTypes = loadRef.current;
    if (!enabled || !fetchTypes) {
      setLoading(false);
      setError(null);
      setOptions([]);
      return;
    }
    const abortController = new AbortController();
    setLoading(true);
    setError(null);
    setOptions([]);
    fetchTypes(abortController, tenantId)
      .then((items) => {
        if (abortController.signal.aborted) return;
        setOptions(
          (Array.isArray(items) ? items : [])
            .filter(
              (item): item is { id: number; name: string } =>
                typeof item.id === 'number'
            )
            .map((item) => ({ id: item.id, name: item.name }))
        );
      })
      .catch((err: unknown) => {
        if (abortController.signal.aborted) return;
        setError(err instanceof Error ? err : new Error(String(err)));
      })
      .finally(() => {
        if (!abortController.signal.aborted) setLoading(false);
      });
    return () => abortController.abort();
  }, [enabled, tenantId, attempt]);

  return {
    options,
    loading: enabled && loading,
    error,
    refetch: () => setAttempt((value) => value + 1),
  };
};
